import type { TeacherScenario, TeacherScenarioCategory } from "@/config/teacherScenarios";

const STORAGE_PREFIX = "heybodhi:custom-scenarios:v1:";
export const customScenariosChangedEvent = "heybodhi:custom-scenarios-changed";

export interface CustomScenarioInput {
  title: string;
  category: TeacherScenarioCategory;
  summary: string;
  openingPrompt: string;
  /** Optional: one thing the teacher wants to get better at — becomes a coaching gate. */
  coachingFocus?: string;
  /** Optional: a mid-scenario twist for Practice mode. */
  twist?: string;
}

export interface StoredCustomScenario extends TeacherScenario {
  isCustom: true;
  createdAt: string;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function isStoredCustomScenario(value: unknown): value is StoredCustomScenario {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<StoredCustomScenario>;
  return (
    typeof v.id === "string" &&
    typeof v.title === "string" &&
    typeof v.openingPrompt === "string" &&
    typeof v.category === "string"
  );
}

function readAll(userId: string): StoredCustomScenario[] {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isStoredCustomScenario);
  } catch {
    return [];
  }
}

function writeAll(userId: string, rows: StoredCustomScenario[]): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(rows));
  } catch {
    /* quota / serialization — ignore */
  }
}

function notifyChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(customScenariosChangedEvent));
}

const DEFAULT_COACHING_GATES = [
  "What is your opening move — and what are you trying to create in the first moment?",
  "What do you need to stay grounded if this gets hard?",
];

const DEFAULT_DEBRIEF_PROMPTS = ["What landed well?", "What would you do differently next time?"];

export function listCustomScenarios(userId: string | null | undefined): StoredCustomScenario[] {
  if (!userId) return [];
  return readAll(userId).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function buildCustomScenario(input: CustomScenarioInput): StoredCustomScenario {
  const coachingFocus = input.coachingFocus?.trim();
  const twist = input.twist?.trim();

  return {
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: input.title.trim(),
    category: input.category,
    summary: input.summary.trim(),
    openingPrompt: input.openingPrompt.trim(),
    coachingGates: coachingFocus
      ? [coachingFocus, DEFAULT_COACHING_GATES[1]!]
      : [...DEFAULT_COACHING_GATES],
    complications: twist ? [twist] : ["A new pressure or emotion enters the moment — adapt in real time."],
    debriefPrompts: [...DEFAULT_DEBRIEF_PROMPTS],
    complicationAfterTurns: 2,
    isCustom: true,
    createdAt: new Date().toISOString(),
  };
}

export function saveCustomScenario(userId: string, input: CustomScenarioInput): StoredCustomScenario {
  const scenario = buildCustomScenario(input);
  const rows = readAll(userId);
  rows.unshift(scenario);
  writeAll(userId, rows);
  notifyChanged();
  return scenario;
}

export function deleteCustomScenario(userId: string, id: string): void {
  writeAll(userId, readAll(userId).filter((s) => s.id !== id));
  notifyChanged();
}
